import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useAuth } from "@/context/auth-context";
import { jsonAuthHeaders } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { useState } from "react";
import { PaginatedResponse } from "../types";
import { DatasetGroup } from "./types";

export const useGetDatasetGroups = ({
  search,
  dataSetId,
  minVersionNumber,
  currentPage,
}: {
  search?: string;
  dataSetId?: string;
  minVersionNumber?: number;
  currentPage?: number;
}) => {
  const { workspaceId } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const authContext = useAuth();

  const { data, isLoading } = useQuery<
    PaginatedResponse<DatasetGroup[], "dataset_groups", string>
  >({
    queryKey: [
      "dataset-groups",
      search,
      currentPage,
      page,
      pageSize,
      dataSetId,
      minVersionNumber,
    ],
    queryFn: async () => {
      const url = new URL(ENDPOINTS.get_dataset_groups(Number(workspaceId)));

      if (search) {
        url.searchParams.set("q", search);
      }

      if (dataSetId) {
        url.searchParams.set("dataset_id", dataSetId);
      }

      if (minVersionNumber !== undefined) {
        url.searchParams.set("min_version_number", minVersionNumber.toString());
      }

      url.searchParams.set("page", (currentPage ?? page).toString());
      url.searchParams.set("per_page", pageSize.toString());

      const response = await fetch(url, {
        headers: jsonAuthHeaders(authContext!.userToken!),
      });

      return handleResponse(response);
    },
    enabled: !!workspaceId,
  });

  return { data, isLoading, page, setPage, pageSize, setPageSize };
};
